const pad2 = (value: number) => String(value).padStart(2, '0');const pad3 = (value: number) => String(value).padStart(3, '0');

// Locale-safe (ASCII digits) formatting for API params.
export const formatLocalDateParam = (date: Date) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
};

/**
 * Today's calendar day in the VIEWER's zone as `YYYY-MM-DD`.
 * Never `new Date().toISOString().split('T')[0]` / `.slice(0, 10)`: that is the UTC day, which is
 * yesterday in the UAE before 04:00 and tomorrow in New York after 20:00.
 */
export const todayLocalDateParam = (now: Date = new Date()) => formatLocalDateParam(now);

/**
 * A DATE-ONLY API value (`2026-09-17`, `2026-09-17T00:00:00`, even a stray `…Z`) as a `Date` at LOCAL
 * midnight of that calendar day. `new Date('2026-09-17')` is UTC midnight (the previous day west of
 * UTC), and `new Date('2026-09-17T00:00:00')` → `toISOString()` is the previous day east of UTC — the
 * calendar fields are read textually instead. Never use for a UTC instant (use `parseApiUtc`).
 */
export const parseApiDateOnly = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * `YYYY-MM-DD` of a DATE-ONLY API value, read textually (for date fields / day keys). A `Date` is read
 * with its LOCAL fields. Empty/invalid → `''`.
 */
export const apiDateOnlyParam = (value?: string | Date | null): string => {
  if (!value) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : formatLocalDateParam(value);
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  if (m) return m[1];
  const d = parseApiDateOnly(value);
  return d ? formatLocalDateParam(d) : '';
};

export const formatLocalDateTimeParam = (date: Date) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  const ms = pad3(date.getMilliseconds());
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}.${ms}`;
};

/**
 * Wall-clock NOW as `YYYY-MM-DDTHH:mm` in an IANA time zone. An empty or non-IANA id (Intl rejects
 * Windows ids such as "Arabian Standard Time") falls back to the viewer's own clock.
 *
 * 🔴 That last sentence is why you should NOT call this with a company time zone. Formatting silently
 * with the VIEWER's clock is wrong for anyone travelling or living outside the company's zone, and it is
 * invisible at the call site. For the company clock use `useCompanyClock()` / `companyWallClockNow()`
 * from `utils/companyClock`, which resolves the zone first and reports which rung of the
 * company → UTC → browser chain it took. `companyClock` is the only caller of this function today;
 * keep it that way unless the zone is already known-good.
 */
export const wallClockNowParam = (timeZone?: string | null, now: Date = new Date()): string => {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(now);
      const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
      const hour = get('hour') === '24' ? '00' : get('hour');
      return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
    } catch {
      // Unknown / non-IANA zone id: fall through to the viewer's clock.
    }
  }
  return formatLocalDateTimeParam(now).slice(0, 16);
};

// The API serialises UTC timestamps WITHOUT a trailing Z: EF returns DateTime with Kind=Unspecified,
// and `new Date('2026-08-31T13:47:00')` reads a bare timestamp as LOCAL time. Every inbound timestamp
// must come through here or it renders off by exactly the viewer's UTC offset.
// Tolerates both forms — the backend is moving instants to a trailing Z (CO-719), so a value that
// already carries Z or an offset is passed through untouched and never shifted twice. A Date is
// returned as-is. Fractional seconds beyond milliseconds (.NET emits 7 digits) are trimmed because
// Safari rejects them. Never feed a DATE-ONLY value here: it would become UTC midnight and roll to
// the previous day west of UTC.
export const parseApiUtc = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const trimmed = value.trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  const parsed = new Date(hasZone ? trimmed : `${trimmed}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// WALL-CLOCK timestamps (tournament kickoffs — `Match.ScheduledDateTime`): the value IS the company's
// local time and must never be shifted by the viewer's UTC offset ("don't show UTC on the UI, just
// local time"). A stray Z/offset suffix is stripped so the stored hour survives regardless of how a
// producer tagged it. Pair with `wallClockDateParam` / `wallClockTimeParam` when writing a picker
// back — never `toISOString()`.

/** Calendar fields of a wall-clock timestamp — no `Date`, so no timezone can touch them. */
export interface WallClockParts {
  year: number;
  month: number; // 1-12
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
}

const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,7}))?)?$/;

/**
 * Split a wall-clock timestamp into its calendar fields WITHOUT constructing a `Date`.
 *
 * `new Date('2026-03-08T02:30:00')` is not lossless: in a viewer's spring-forward DST gap that wall
 * time does not exist, so the browser silently rolls it forward (America/New_York renders 03:30) and
 * an edit form would then persist the shifted hour back to the server. Reading the fields textually
 * makes parse → format a stable round-trip in every zone.
 */
export const parseApiWallClockParts = (value?: string | null): WallClockParts | null => {
  if (!value) return null;
  const bare = value.trim().replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const m = WALL_CLOCK_RE.exec(bare);
  if (!m) return null;

  const [, y, mo, d, hh, mm, ss, frac] = m;
  const parts: WallClockParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hours: Number(hh ?? '0'),
    minutes: Number(mm ?? '0'),
    seconds: Number(ss ?? '0'),
    milliseconds: frac ? Number(frac.slice(0, 3).padEnd(3, '0')) : 0,
  };

  const valid =
    parts.month >= 1 && parts.month <= 12 &&
    parts.day >= 1 && parts.day <= 31 &&
    parts.hours <= 23 && parts.minutes <= 59 && parts.seconds <= 59;
  return valid ? parts : null;
};

export const parseApiWallClock = (value?: string | null): Date | null => {
  if (!value) return null;
  const parts = parseApiWallClockParts(value);
  if (parts) {
    // Field-wise construction — never `new Date(string)`, whose bare-string branch is what the DST
    // gap corrupts. (A gap wall time still cannot be *represented* as a local Date; the param
    // helpers below therefore read the parts directly and never round-trip through this Date.)
    const d = new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hours,
      parts.minutes,
      parts.seconds,
      parts.milliseconds,
    );
    if (parts.year < 100) d.setFullYear(parts.year); // 0-99 would map to 1900+y
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const bare = value.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const parsed = new Date(bare);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** `HH:mm` of a wall-clock timestamp, for `<input type="time">`. */
export const wallClockTimeParam = (value?: string | null): string => {
  const parts = parseApiWallClockParts(value);
  if (parts) return `${pad2(parts.hours)}:${pad2(parts.minutes)}`;
  const d = parseApiWallClock(value);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
};

/** `YYYY-MM-DD` of a wall-clock timestamp, for `<input type="date">` (NOT toISOString — no UTC shift). */
export const wallClockDateParam = (value?: string | null): string => {
  const parts = parseApiWallClockParts(value);
  if (parts) return `${String(parts.year).padStart(4, '0')}-${pad2(parts.month)}-${pad2(parts.day)}`;
  const d = parseApiWallClock(value);
  return d ? formatLocalDateParam(d) : '';
};
